/* ─────────────────────────────────────────────────────────
 * Offline Writing Lessons — organised by skill level (1-5)
 * Interactive writing exercises: tracing, spelling, sentence building
 * ───────────────────────────────────────────────────────── */

import type { SkillLevel } from './skillLevel';

export interface WritingLessonSummary {
  id: string;
  title: string;
  titleFr: string;
  difficulty: number;
  level: SkillLevel;
}

export interface WritingBlock {
  type: 'prompt' | 'spelling' | 'fill-blank' | 'sentence-build' | 'free-write' | 'text';
  content: string;
  contentFr?: string;
  answer?: string;
  answerFr?: string;
  options?: string[];
  optionsFr?: string[];
}

export interface WritingLessonDetail {
  id: string;
  title: string;
  titleFr: string;
  level: SkillLevel;
  contentBlocks: WritingBlock[];
}

export const WRITING_LESSONS: WritingLessonDetail[] = [
  // ── Level 1: Starter ──
  {
    id: 'W1-01', title: 'Write Your First Letters', titleFr: 'Écris tes premières lettres', level: 1,
    contentBlocks: [
      { type: 'text', content: "Let's practice writing letters! Type the letter you see.", contentFr: "Pratiquons l'écriture de lettres ! Tape la lettre que tu vois." },
      { type: 'spelling', content: 'Type the letter: A', contentFr: 'Tape la lettre : A', answer: 'A', answerFr: 'A' },
      { type: 'spelling', content: 'Type the letter: B', contentFr: 'Tape la lettre : B', answer: 'B', answerFr: 'B' },
      { type: 'spelling', content: 'Type the letter: C', contentFr: 'Tape la lettre : C', answer: 'C', answerFr: 'C' },
      { type: 'spelling', content: 'Type the letter: D', contentFr: 'Tape la lettre : D', answer: 'D', answerFr: 'D' },
      { type: 'spelling', content: 'Type the letter: E', contentFr: 'Tape la lettre : E', answer: 'E', answerFr: 'E' },
      { type: 'text', content: 'Great job writing your letters! 🎉', contentFr: 'Bravo pour tes lettres ! 🎉' },
    ],
  },
  {
    id: 'W1-02', title: 'Spell Simple Words', titleFr: 'Épelle des mots simples', level: 1,
    contentBlocks: [
      { type: 'text', content: "Can you spell these simple words? Type each word!", contentFr: "Peux-tu épeler ces mots simples ? Tape chaque mot !" },
      { type: 'spelling', content: 'Spell the word for 🐱', contentFr: 'Épelle le mot pour 🐱', answer: 'cat', answerFr: 'chat' },
      { type: 'spelling', content: 'Spell the word for 🐶', contentFr: 'Épelle le mot pour 🐶', answer: 'dog', answerFr: 'chien' },
      { type: 'spelling', content: 'Spell the word for ☀️', contentFr: 'Épelle le mot pour ☀️', answer: 'sun', answerFr: 'soleil' },
      { type: 'spelling', content: 'Spell the word for 🏃', contentFr: 'Épelle le mot pour 🏃', answer: 'run', answerFr: 'courir' },
      { type: 'text', content: 'Amazing spelling! You wrote 4 words! ⭐', contentFr: 'Super orthographe ! Tu as écrit 4 mots ! ⭐' },
    ],
  },
  {
    id: 'W1-03', title: 'Missing Letters', titleFr: 'Lettres manquantes', level: 1,
    contentBlocks: [
      { type: 'text', content: "Fill in the missing letter to complete the word!", contentFr: "Remplis la lettre manquante pour compléter le mot !" },
      { type: 'fill-blank', content: '_at (like a 🐱)', contentFr: '_hat (comme un 🐱)', answer: 'c', answerFr: 'c' },
      { type: 'fill-blank', content: '_ig (something large)', contentFr: '_rand (quelque chose de grand)', answer: 'b', answerFr: 'g' },
      { type: 'fill-blank', content: 'su_ (in the sky ☀️)', contentFr: 'sole_l (dans le ciel ☀️)', answer: 'n', answerFr: 'i' },
      { type: 'fill-blank', content: 'do_ (a pet 🐶)', contentFr: 'chie_ (un animal 🐶)', answer: 'g', answerFr: 'n' },
      { type: 'text', content: 'You filled all the blanks! 🌟', contentFr: 'Tu as rempli tous les blancs ! 🌟' },
    ],
  },
  // ── Level 2: Explorer ──
  {
    id: 'W2-01', title: 'Word Building', titleFr: 'Construction de mots', level: 2,
    contentBlocks: [
      { type: 'text', content: "Build words by choosing the right letters!", contentFr: "Construis des mots en choisissant les bonnes lettres !" },
      { type: 'spelling', content: 'Spell: HAND ✋', contentFr: 'Épelle: MAIN ✋', answer: 'hand', answerFr: 'main' },
      { type: 'spelling', content: 'Spell: BOOK 📖', contentFr: 'Épelle: LIVRE 📖', answer: 'book', answerFr: 'livre' },
      { type: 'spelling', content: 'Spell: TREE 🌳', contentFr: 'Épelle: ARBRE 🌳', answer: 'tree', answerFr: 'arbre' },
      { type: 'spelling', content: 'Spell: STAR ⭐', contentFr: 'Épelle: ÉTOILE ⭐', answer: 'star', answerFr: 'etoile' },
      { type: 'text', content: 'Excellent word building! 🚀', contentFr: 'Excellente construction de mots ! 🚀' },
    ],
  },
  {
    id: 'W2-02', title: 'Sentence Completion', titleFr: 'Compléter des phrases', level: 2,
    contentBlocks: [
      { type: 'text', content: "Pick the word that best completes the sentence!", contentFr: "Choisis le mot qui complète le mieux la phrase !" },
      { type: 'fill-blank', content: 'The cat is on the ___.', contentFr: 'Le chat est sur le ___.', answer: 'mat', answerFr: 'tapis', options: ['mat', 'bat', 'rat'], optionsFr: ['tapis', 'lit', 'sol'] },
      { type: 'fill-blank', content: 'I like to ___ books.', contentFr: "J'aime ___ des livres.", answer: 'read', answerFr: 'lire', options: ['read', 'red', 'ride'], optionsFr: ['lire', 'voir', 'dire'] },
      { type: 'fill-blank', content: 'The sun is very ___.', contentFr: 'Le soleil est très ___.', answer: 'hot', answerFr: 'chaud', options: ['hot', 'hop', 'hat'], optionsFr: ['chaud', 'froid', 'grand'] },
      { type: 'text', content: 'Great sentence work! 📝', contentFr: 'Super travail de phrases ! 📝' },
    ],
  },
  {
    id: 'W2-03', title: 'Rhyme Writing', titleFr: 'Écriture de rimes', level: 2,
    contentBlocks: [
      { type: 'text', content: "Write a word that rhymes with the given word!", contentFr: "Écris un mot qui rime avec le mot donné !" },
      { type: 'spelling', content: 'Write a word that rhymes with CAT', contentFr: 'Écris un mot qui rime avec CHAT', answer: 'hat,bat,mat,sat,fat,rat,pat,flat', answerFr: 'rat,mat,plat' },
      { type: 'spelling', content: 'Write a word that rhymes with DOG', contentFr: 'Écris un mot qui rime avec MOI', answer: 'log,fog,hog,jog,frog,bog', answerFr: 'roi,toi,quoi,bois' },
      { type: 'spelling', content: 'Write a word that rhymes with CAKE', contentFr: 'Écris un mot qui rime avec GÂTEAU', answer: 'lake,make,bake,take,shake,wake,fake,sake', answerFr: 'bateau,chateau,chapeau,cadeau' },
      { type: 'text', content: 'You are a rhyme master! 🎵', contentFr: 'Tu es un maître des rimes ! 🎵' },
    ],
  },
  // ── Level 3: Builder ──
  {
    id: 'W3-01', title: 'Sentence Building', titleFr: 'Construire des phrases', level: 3,
    contentBlocks: [
      { type: 'text', content: "Put the words in the right order to make a sentence!", contentFr: "Mets les mots dans le bon ordre pour faire une phrase !" },
      { type: 'sentence-build', content: 'is / The / big / cat', contentFr: 'est / Le / grand / chat', answer: 'The cat is big', answerFr: 'Le chat est grand' },
      { type: 'sentence-build', content: 'run / I / fast / can', contentFr: 'courir / Je / vite / peux', answer: 'I can run fast', answerFr: 'Je peux courir vite' },
      { type: 'sentence-build', content: 'likes / She / to / read', contentFr: 'aime / Elle / lire', answer: 'She likes to read', answerFr: 'Elle aime lire' },
      { type: 'text', content: 'Awesome sentence building! ✨', contentFr: 'Super construction de phrases ! ✨' },
    ],
  },
  {
    id: 'W3-02', title: 'Story Starter', titleFr: 'Début d\'histoire', level: 3,
    contentBlocks: [
      { type: 'text', content: "Finish the sentence to start a story! Be creative!", contentFr: "Finis la phrase pour commencer une histoire ! Sois créatif !" },
      { type: 'free-write', content: 'Once upon a time, there was a...', contentFr: 'Il était une fois, il y avait un...' },
      { type: 'free-write', content: 'The friendly dragon liked to...', contentFr: 'Le dragon gentil aimait...' },
      { type: 'free-write', content: 'One sunny morning, the children decided to...', contentFr: 'Un matin ensoleillé, les enfants ont décidé de...' },
      { type: 'text', content: 'What an amazing imagination! 🌈', contentFr: 'Quelle imagination incroyable ! 🌈' },
    ],
  },
  // ── Level 4: Reader ──
  {
    id: 'W4-01', title: 'Paragraph Writing', titleFr: 'Écriture de paragraphes', level: 4,
    contentBlocks: [
      { type: 'text', content: "Write about the topic in 2-3 sentences!", contentFr: "Écris sur le sujet en 2-3 phrases !" },
      { type: 'free-write', content: 'Write about your favorite animal. What does it look like? Why do you like it?', contentFr: 'Écris à propos de ton animal préféré. À quoi ressemble-t-il ? Pourquoi l\'aimes-tu ?' },
      { type: 'free-write', content: 'Describe your best day ever. What happened? Who was there?', contentFr: 'Décris ta meilleure journée. Que s\'est-il passé ? Qui était là ?' },
      { type: 'text', content: 'Beautiful writing! You are becoming a real author! 📖', contentFr: 'Belle écriture ! Tu deviens un vrai auteur ! 📖' },
    ],
  },
  {
    id: 'W4-02', title: 'Letter Writing', titleFr: 'Écriture de lettres', level: 4,
    contentBlocks: [
      { type: 'text', content: "Let's write a short letter to a friend!", contentFr: "Écrivons une courte lettre à un ami !" },
      { type: 'prompt', content: 'Start your letter: Dear ___,', contentFr: 'Commence ta lettre : Cher/Chère ___,' },
      { type: 'free-write', content: 'Write 2-3 sentences to your friend about something fun you did.', contentFr: 'Écris 2-3 phrases à ton ami sur quelque chose d\'amusant que tu as fait.' },
      { type: 'prompt', content: 'End your letter: Your friend, ___', contentFr: 'Termine ta lettre : Ton ami(e), ___' },
      { type: 'text', content: 'What a wonderful letter! Your friend will love it! 💌', contentFr: 'Quelle belle lettre ! Ton ami va l\'adorer ! 💌' },
    ],
  },
  // ── Level 5: Champion ──
  {
    id: 'W5-01', title: 'Creative Story Writing', titleFr: 'Écriture créative', level: 5,
    contentBlocks: [
      { type: 'text', content: "Write a short story using the prompt below! Try to write at least 5 sentences.", contentFr: "Écris une courte histoire avec le sujet ci-dessous ! Essaie d'écrire au moins 5 phrases." },
      { type: 'free-write', content: 'The old treasure map led to a mysterious island where...', contentFr: 'La vieille carte au trésor menait à une île mystérieuse où...' },
      { type: 'text', content: 'Great story! Now try to add dialogue — characters talking to each other.', contentFr: 'Belle histoire ! Maintenant essaie d\'ajouter du dialogue — des personnages qui se parlent.' },
      { type: 'free-write', content: 'Continue your story. Make your characters talk! Use "quotation marks".', contentFr: 'Continue ton histoire. Fais parler tes personnages ! Utilise des « guillemets ».' },
      { type: 'text', content: 'You are a talented writer! Keep creating stories! ✍️🏆', contentFr: 'Tu es un écrivain talentueux ! Continue de créer des histoires ! ✍️🏆' },
    ],
  },
  {
    id: 'W5-02', title: 'Opinion Writing', titleFr: 'Écriture d\'opinion', level: 5,
    contentBlocks: [
      { type: 'text', content: "Share your opinion! Write what you think and explain why.", contentFr: "Partage ton opinion ! Écris ce que tu penses et explique pourquoi." },
      { type: 'free-write', content: 'Do you think recess should be longer? Write 3+ sentences explaining your opinion.', contentFr: 'La récréation devrait-elle être plus longue ? Écris 3+ phrases pour expliquer ton opinion.' },
      { type: 'free-write', content: 'What is the best book you have ever read? Why should others read it too?', contentFr: 'Quel est le meilleur livre que tu aies lu ? Pourquoi d\'autres devraient-ils le lire aussi ?' },
      { type: 'text', content: 'Excellent reasoning! You expressed your ideas clearly! 🎯', contentFr: 'Excellent raisonnement ! Tu as bien exprimé tes idées ! 🎯' },
    ],
  },
];

/** Get writing lessons filtered by skill level */
export function getWritingLessonsForLevel(level: number): WritingLessonSummary[] {
  return WRITING_LESSONS
    .filter(l => l.level <= level)
    .map(l => ({ id: l.id, title: l.title, titleFr: l.titleFr, difficulty: l.level, level: l.level }));
}

/** Get a specific writing lesson by ID */
export function getWritingLessonById(id: string): WritingLessonDetail | undefined {
  return WRITING_LESSONS.find(l => l.id === id);
}

/* ── Practice Word Generator ──
 * Generates randomized practice words for a given level so content isn't always the same.
 */
const WORD_POOLS: Record<number, { en: string[]; fr: string[] }> = {
  1: {
    en: ['apple', 'ball', 'cat', 'dog', 'egg', 'fish', 'girl', 'hat', 'ice', 'jam', 'kite', 'lamp', 'moon', 'nest', 'owl', 'pen', 'rain', 'sun', 'tree', 'van'],
    fr: ['pomme', 'balle', 'chat', 'chien', 'oeuf', 'poisson', 'fille', 'chapeau', 'glace', 'confiture', 'cerf-volant', 'lampe', 'lune', 'nid', 'hibou', 'stylo', 'pluie', 'soleil', 'arbre', 'camion'],
  },
  2: {
    en: ['hand', 'book', 'tree', 'star', 'cake', 'lake', 'house', 'flower', 'cloud', 'bread', 'stone', 'green', 'smile', 'dream', 'light', 'world', 'happy', 'water', 'tiger', 'piano'],
    fr: ['main', 'livre', 'arbre', 'étoile', 'gâteau', 'lac', 'maison', 'fleur', 'nuage', 'pain', 'pierre', 'vert', 'sourire', 'rêve', 'lumière', 'monde', 'heureux', 'eau', 'tigre', 'piano'],
  },
  3: {
    en: ['beautiful', 'adventure', 'elephant', 'birthday', 'chocolate', 'butterfly', 'kangaroo', 'dinosaur', 'umbrella', 'treasure', 'mountain', 'hospital', 'sandwich', 'suddenly', 'together', 'different'],
    fr: ['magnifique', 'aventure', 'éléphant', 'anniversaire', 'chocolat', 'papillon', 'kangourou', 'dinosaure', 'parapluie', 'trésor', 'montagne', 'hôpital', 'sandwich', 'soudain', 'ensemble', 'différent'],
  },
  4: {
    en: ['environment', 'imagination', 'temperature', 'photograph', 'experiment', 'underground', 'communicate', 'celebration', 'opportunity', 'comfortable'],
    fr: ['environnement', 'imagination', 'température', 'photographie', 'expérience', 'souterrain', 'communiquer', 'célébration', 'opportunité', 'confortable'],
  },
  5: {
    en: ['responsibility', 'extraordinary', 'encyclopedia', 'sophisticated', 'accomplishment', 'determination', 'collaboration', 'independence', 'understanding', 'appreciation'],
    fr: ['responsabilité', 'extraordinaire', 'encyclopédie', 'sophistiqué', 'accomplissement', 'détermination', 'collaboration', 'indépendance', 'compréhension', 'appréciation'],
  },
};

const MIN_WRITING_LESSONS_PER_LEVEL = 30;

function buildSupplementalWritingLessons(): WritingLessonDetail[] {
  const lessons: WritingLessonDetail[] = [];

  for (let level = 1 as SkillLevel; level <= 5; level = (level + 1) as SkillLevel) {
    const existing = WRITING_LESSONS.filter(lesson => lesson.level === level);
    const needed = Math.max(0, MIN_WRITING_LESSONS_PER_LEVEL - existing.length);
    const pool = WORD_POOLS[level] || WORD_POOLS[1];

    for (let i = 0; i < needed; i++) {
      const lessonNumber = existing.length + i + 1;
      const enWord = pool.en[i % pool.en.length];
      const frWord = pool.fr[i % pool.fr.length];
      const nextEn = pool.en[(i + 3) % pool.en.length];
      const nextFr = pool.fr[(i + 3) % pool.fr.length];
      const thirdEn = pool.en[(i + 6) % pool.en.length];
      const thirdFr = pool.fr[(i + 6) % pool.fr.length];

      lessons.push({
        id: `W${level}-${String(lessonNumber).padStart(2, '0')}`,
        title: `Writing Practice ${lessonNumber}`,
        titleFr: `Pratique d'écriture ${lessonNumber}`,
        level,
        contentBlocks: [
          {
            type: 'text',
            content: `Practice level ${level} writing with clear spelling, word choice, and sentence work.`,
            contentFr: `Pratique l'écriture niveau ${level} avec l'orthographe, le choix des mots et les phrases.`,
          },
          {
            type: 'spelling',
            content: `Spell: ${enWord.toUpperCase()}`,
            contentFr: `Épelle : ${frWord.toUpperCase()}`,
            answer: enWord,
            answerFr: frWord,
          },
          {
            type: 'fill-blank',
            content: `Complete the word: ${nextEn.slice(0, Math.max(1, nextEn.length - 1))}_`,
            contentFr: `Complète le mot : ${nextFr.slice(0, Math.max(1, nextFr.length - 1))}_`,
            answer: nextEn.at(-1) || nextEn,
            answerFr: nextFr.at(-1) || nextFr,
          },
          {
            type: 'sentence-build',
            content: `I / can / write / ${thirdEn}`,
            contentFr: `Je / peux / écrire / ${thirdFr}`,
            answer: `I can write ${thirdEn}`,
            answerFr: `Je peux écrire ${thirdFr}`,
          },
          {
            type: 'text',
            content: 'Nice writing practice. Keep building careful words.',
            contentFr: "Belle pratique d'écriture. Continue à construire des mots avec soin.",
          },
        ],
      });
    }
  }

  return lessons;
}

WRITING_LESSONS.push(...buildSupplementalWritingLessons());

/** Generate randomized practice words for a level */
export function generatePracticeWords(level: number, count = 5, locale = 'en'): string[] {
  const pool = WORD_POOLS[level] || WORD_POOLS[1];
  const words = locale.startsWith('fr') ? pool.fr : pool.en;
  const shuffled = [...words].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/* ── Original content backup for redo ── */
const originalWritingBlocks = new Map<string, WritingBlock[]>();
const regeneratedWritingLevels = new Set<number>();

export function isWritingLevelRegenerated(level: number): boolean {
  return regeneratedWritingLevels.has(level);
}

/** Check if a specific writing lesson (by ID) belongs to a regenerated level */
export function isWritingLessonRegenerated(lessonId: string): boolean {
  const lesson = WRITING_LESSONS.find(l => l.id === lessonId);
  return lesson ? regeneratedWritingLevels.has(lesson.level) : false;
}

/**
 * Regenerate ALL lessons for a level with new random words.
 * Returns the first lesson ID so the caller can navigate to it.
 */
export function regenerateWritingLesson(level: number, count = 5, locale = 'en'): string {
  const isFr = locale.startsWith('fr');
  const pool = WORD_POOLS[level] || WORD_POOLS[1];
  const lessons = WRITING_LESSONS.filter(l => l.level === level);
  if (lessons.length === 0) return '';

  for (const lesson of lessons) {
    if (!originalWritingBlocks.has(lesson.id)) {
      originalWritingBlocks.set(lesson.id, lesson.contentBlocks.map(b => ({ ...b })));
    }
    const enWords = [...pool.en].sort(() => Math.random() - 0.5).slice(0, count);
    const frWords = enWords.map(w => {
      const idx = pool.en.indexOf(w);
      return idx >= 0 ? pool.fr[idx] : w;
    });

    const newBlocks: WritingBlock[] = [
      lesson.contentBlocks[0],
      ...enWords.map((word, i) => ({
        type: 'spelling' as const,
        content: isFr ? `Spell: ${frWords[i]}` : `Spell: ${word}`,
        contentFr: `Épelle : ${frWords[i]}`,
        answer: word,
        answerFr: frWords[i],
      })),
      { type: 'text' as const, content: 'Great practice! Keep going! 🌟', contentFr: 'Belle pratique ! Continue ! 🌟' },
    ];

    lesson.contentBlocks = newBlocks;
  }

  regeneratedWritingLevels.add(level);
  return lessons[0].id;
}

/** Restore original content for all lessons at a level */
export function restoreWritingLessons(level: number): void {
  const lessons = WRITING_LESSONS.filter(l => l.level === level);
  for (const lesson of lessons) {
    const orig = originalWritingBlocks.get(lesson.id);
    if (orig) {
      lesson.contentBlocks = orig.map(b => ({ ...b }));
    }
  }
  regeneratedWritingLevels.delete(level);
}
